import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, 
  Users, 
  ShoppingBag, 
  Store, 
  Settings, 
  LogOut,
  Bell,
  Menu,
  X,
  IndianRupee,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Search,
  MapPin,
  Phone,
  Mail,
  FileText,
  RefreshCw,
  Truck,
  Eye,
  Bike,
  User,
  Ban,
  Send,
  Percent,
  Receipt,
  TrendingUp,
  Link,
  Building2,
  ArrowUpDown
} from 'lucide-react';
import { RestaurantApplication, RestaurantProfile, Order, PlatformStats, DeliveryPartner, DeliveryArea, Settlement, PlatformCommissionSettings } from '../types';
import {
  getAllApplications,
  getAllRestaurants,
  getAllOrders,
  approveApplication,
  rejectApplication,
  toggleRestaurantStatus,
  getPlatformStats,
  getAllDeliveryPartners,
  approveDeliveryPartner,
  rejectDeliveryPartner,
  toggleDeliveryPartnerStatus,
  getAllDeliveryAreas,
  createDeliveryArea,
  updateDeliveryArea,
  deleteDeliveryArea,
  updateRestaurantCommission,
  getPlatformCommissionSettings,
  updatePlatformCommissionSettings,
  getAllSettlements,
  generateSettlement,
  updateSettlementStatus,
  createLinkedAccount,
  getAllCustomers,
} from '../services/adminService';
import {
  getDeliverySettings,
  updateDeliverySettings,
  type AppDeliverySettings,
  getAppUsers,
  sendNotification,
  type AppType,
  type AppUser,
  type SendNotificationPayload,
} from '../services/adminService';

type Props = {
  adminUid: string;
  onLogout: () => void;
};

type Tab = 'overview' | 'applications' | 'restaurants' | 'orders' | 'delivery_partners' | 'delivery_areas' | 'commission' | 'settlements' | 'settings' | 'notifications';

export default function Dashboard({ adminUid, onLogout }: Props) {
  const [currentTab, setCurrentTab] = useState<Tab>('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [applications, setApplications] = useState<RestaurantApplication[]>([]);
  const [restaurants, setRestaurants] = useState<RestaurantProfile[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [deliveryPartners, setDeliveryPartners] = useState<DeliveryPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Application detail view
  const [selectedApplication, setSelectedApplication] = useState<RestaurantApplication | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Delivery Partner detail view
  const [selectedPartner, setSelectedPartner] = useState<DeliveryPartner | null>(null);
  const [dpRejectionReason, setDpRejectionReason] = useState('');
  const [showDpRejectModal, setShowDpRejectModal] = useState(false);
  const [dpFilter, setDpFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  // Notification state
  const [notifApps, setNotifApps] = useState<AppType[]>([]);
  const [notifUserMode, setNotifUserMode] = useState<'all' | 'specific'>('all');
  const [notifUsers, setNotifUsers] = useState<AppUser[]>([]);
  const [notifUsersLoading, setNotifUsersLoading] = useState(false);
  const [notifSelectedUsers, setNotifSelectedUsers] = useState<string[]>([]);
  const [notifUserSearch, setNotifUserSearch] = useState('');
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');
  const [notifImageUrl, setNotifImageUrl] = useState('');
  const [notifSending, setNotifSending] = useState(false);
  const [notifResult, setNotifResult] = useState<{ success: boolean; message: string } | null>(null);

  // Settings state
  const [deliverySettings, setDeliverySettings] = useState<AppDeliverySettings>({ maxOrderLimit: 5, deliveryRadius: 5 });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  // Delivery Areas state
  const [deliveryAreas, setDeliveryAreas] = useState<DeliveryArea[]>([]);
  const [newAreaName, setNewAreaName] = useState('');
  const [newAreaCity, setNewAreaCity] = useState('');
  const [newAreaPincode, setNewAreaPincode] = useState('');
  const [areaActionLoading, setAreaActionLoading] = useState<string | null>(null);

  // Commission state
  const [commissionSettings, setCommissionSettings] = useState<PlatformCommissionSettings>({
    defaultCommissionPercentage: 10, minCommissionPercentage: 5,
    maxCommissionPercentage: 40, deliveryPartnerPayPerOrder: 30, deliveryPartnerPayPerKm: 7,
  });
  const [editingCommission, setEditingCommission] = useState<{ id: string; value: number } | null>(null);
  const [commissionSaving, setCommissionSaving] = useState(false);

  // Settlement state  
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [settlementRestaurant, setSettlementRestaurant] = useState('');
  const [settlementPeriodStart, setSettlementPeriodStart] = useState('');
  const [settlementPeriodEnd, setSettlementPeriodEnd] = useState('');
  const [settlementGenerating, setSettlementGenerating] = useState(false);

  // Linked account creation
  const [linkingAccount, setLinkingAccount] = useState<string | null>(null);

  // Filters
  const [appFilter, setAppFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  // Load users whenever app selection changes and mode is specific
  useEffect(() => {
    if (notifUserMode === 'specific' && notifApps.length > 0) {
      setNotifUsersLoading(true);
      getAppUsers(notifApps)
        .then(users => setNotifUsers(users))
        .catch(err => console.error('[Notify] Failed to load users:', err))
        .finally(() => setNotifUsersLoading(false));
    } else {
      setNotifUsers([]);
      setNotifSelectedUsers([]);
    }
  }, [notifApps, notifUserMode]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, appsData, restsData, ordersData, dpData, deliverySettingsData, areasData, commissionSettingsData, settlementsData] = await Promise.all([
        getPlatformStats(),
        getAllApplications(),
        getAllRestaurants(),
        getAllOrders(),
        getAllDeliveryPartners(),
        getDeliverySettings(),
        getAllDeliveryAreas(),
        getPlatformCommissionSettings(),
        getAllSettlements(),
      ]);
      
      setStats(statsData);
      setApplications(appsData);
      setRestaurants(restsData);
      setOrders(ordersData);
      setDeliveryPartners(dpData);
      setDeliverySettings(deliverySettingsData);
      setDeliveryAreas(areasData);
      setCommissionSettings(commissionSettingsData);
      setSettlements(settlementsData);
    } catch (error) {
      console.error('[Dashboard] Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Delivery Partner handlers
  const handleApprovePartner = async (partner: DeliveryPartner) => {
    if (!confirm('Are you sure you want to approve this delivery partner?')) return;

    setActionLoading(partner.uid);
    try {
      await approveDeliveryPartner(partner.uid, adminUid);
      await loadData();
      setSelectedPartner(null);
      alert('Delivery partner approved successfully!');
    } catch (error: any) {
      alert('Error approving delivery partner: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectPartner = async () => {
    if (!selectedPartner) return;
    if (!dpRejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    setActionLoading(selectedPartner.uid);
    try {
      await rejectDeliveryPartner(selectedPartner.uid, adminUid, dpRejectionReason);
      await loadData();
      setSelectedPartner(null);
      setShowDpRejectModal(false);
      setDpRejectionReason('');
      alert('Delivery partner rejected');
    } catch (error: any) {
      alert('Error rejecting delivery partner: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleTogglePartnerStatus = async (partner: DeliveryPartner) => {
    setActionLoading(partner.uid);
    try {
      await toggleDeliveryPartnerStatus(partner.uid, !partner.isActive);
      const updatedPartner = { ...partner, isActive: !partner.isActive };
      setDeliveryPartners(deliveryPartners.map(p =>
        p.uid === partner.uid ? updatedPartner : p
      ));
      // Update selected partner if it's the same
      if (selectedPartner?.uid === partner.uid) {
        setSelectedPartner(updatedPartner);
      }
    } catch (error: any) {
      alert('Error updating partner status: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async (application: RestaurantApplication) => {
    if (!confirm('Are you sure you want to approve this application?')) return;

    setActionLoading(application.id);
    try {
      await approveApplication(application.id, adminUid);
      await loadData();
      setSelectedApplication(null);
      alert('Application approved successfully!');
    } catch (error: any) {
      alert('Error approving application: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!selectedApplication) return;
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    setActionLoading(selectedApplication.id);
    try {
      await rejectApplication(selectedApplication.id, adminUid, rejectionReason);
      await loadData();
      setSelectedApplication(null);
      setShowRejectModal(false);
      setRejectionReason('');
      alert('Application rejected');
    } catch (error: any) {
      alert('Error rejecting application: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleRestaurantStatus = async (restaurant: RestaurantProfile) => {
    setActionLoading(restaurant.id);
    try {
      await toggleRestaurantStatus(restaurant.id, !restaurant.isActive);
      setRestaurants(restaurants.map(r => 
        r.id === restaurant.id ? { ...r, isActive: !r.isActive } : r
      ));
    } catch (error: any) {
      alert('Error updating restaurant status: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesFilter = appFilter === 'all' || app.status === appFilter;
    const matchesSearch = !searchQuery || 
      app.restaurantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.ownerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const filteredRestaurants = restaurants.filter(r => 
    !searchQuery || 
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.location.city.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDeliveryPartners = deliveryPartners.filter(dp => {
    const matchesFilter = dpFilter === 'all' || dp.verificationStatus === dpFilter;
    const matchesSearch = !searchQuery || 
      dp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dp.phoneNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dp.vehicleNumber?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-72 bg-gray-900 text-white
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                  <Store className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Lomra Admin</h1>
                  <p className="text-xs text-gray-400">Management Portal</p>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {[
              { id: 'overview', label: 'Overview', icon: LayoutDashboard },
              { id: 'applications', label: 'Applications', icon: FileText, badge: stats?.pendingApplications },
              { id: 'delivery_partners', label: 'Delivery Partners', icon: Truck, badge: stats?.pendingDeliveryPartnerApplications },
              { id: 'delivery_areas', label: 'Delivery Areas', icon: MapPin },
              { id: 'restaurants', label: 'Restaurants', icon: Store },
              { id: 'orders', label: 'Orders', icon: ShoppingBag },
              { id: 'commission', label: 'Commission', icon: Percent },
              { id: 'settlements', label: 'Settlements', icon: Receipt, badge: stats?.pendingSettlements },
              { id: 'notifications', label: 'Notifications', icon: Bell },
              { id: 'settings', label: 'Settings', icon: Settings },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setCurrentTab(item.id as Tab);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                  currentTab === item.id
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </div>
                {item.badge ? (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {item.badge}
                  </span>
                ) : null}
              </button>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-gray-800">
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-gray-800 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              >
                <Menu className="w-6 h-6 text-gray-700" />
              </button>
              <div>
                <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
                  {currentTab.charAt(0).toUpperCase() + currentTab.slice(1)}
                </h2>
                <p className="text-sm text-gray-500 hidden sm:block">
                  {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button 
                onClick={loadData}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh Data"
              >
                <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button className="relative p-2 hover:bg-gray-100 rounded-lg">
                <Bell className="w-6 h-6 text-gray-600" />
                {(stats?.pendingApplications || 0) > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {stats?.pendingApplications}
                  </span>
                )}
              </button>
              <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-white font-bold">
                A
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <AnimatePresence mode="wait">
            {/* Overview Tab */}
            {currentTab === 'overview' && stats && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                  <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Store className="w-5 h-5 text-orange-600" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.totalRestaurants}</p>
                    <p className="text-sm text-gray-500 mt-1">Total Restaurants</p>
                    <p className="text-xs text-green-600 mt-2">{stats.activeRestaurants} active</p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.pendingApplications}</p>
                    <p className="text-sm text-gray-500 mt-1">Pending Applications</p>
                    <p className="text-xs text-yellow-600 mt-2">Needs review</p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <ShoppingBag className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.totalOrders}</p>
                    <p className="text-sm text-gray-500 mt-1">Total Orders</p>
                    <p className="text-xs text-blue-600 mt-2">{stats.todayOrders} today</p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <IndianRupee className="w-5 h-5 text-green-600" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">₹{stats.totalRevenue.toLocaleString()}</p>
                    <p className="text-sm text-gray-500 mt-1">Total Revenue</p>
                    <p className="text-xs text-green-600 mt-2">₹{stats.todayRevenue.toLocaleString()} today</p>
                  </div>
                </div>

                {/* Second Stats Row - Delivery Partners */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                  <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Truck className="w-5 h-5 text-purple-600" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.totalDeliveryPartners || 0}</p>
                    <p className="text-sm text-gray-500 mt-1">Delivery Partners</p>
                    <p className="text-xs text-purple-600 mt-2">{stats.activeDeliveryPartners || 0} active</p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 bg-teal-100 rounded-lg">
                        <Users className="w-5 h-5 text-teal-600" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.onlineDeliveryPartners || 0}</p>
                    <p className="text-sm text-gray-500 mt-1">Online Now</p>
                    <p className="text-xs text-teal-600 mt-2">Ready for delivery</p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 bg-pink-100 rounded-lg">
                        <Clock className="w-5 h-5 text-pink-600" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.pendingDeliveryPartnerApplications || 0}</p>
                    <p className="text-sm text-gray-500 mt-1">Pending Partners</p>
                    <p className="text-xs text-pink-600 mt-2">Awaiting approval</p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <Store className="w-5 h-5 text-indigo-600" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.ordersByRestaurant?.length || 0}</p>
                    <p className="text-sm text-gray-500 mt-1">Active Restaurants</p>
                    <p className="text-xs text-indigo-600 mt-2">With orders</p>
                  </div>
                </div>

                {/* Third Stats Row - Commission & Settlements */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                  <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <Percent className="w-5 h-5 text-emerald-600" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">₹{(stats.totalPlatformCommission || 0).toLocaleString()}</p>
                    <p className="text-sm text-gray-500 mt-1">Platform Commission</p>
                    <p className="text-xs text-emerald-600 mt-2">Total earned</p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 bg-cyan-100 rounded-lg">
                        <Building2 className="w-5 h-5 text-cyan-600" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">₹{(stats.totalRestaurantPayouts || 0).toLocaleString()}</p>
                    <p className="text-sm text-gray-500 mt-1">Restaurant Payouts</p>
                    <p className="text-xs text-cyan-600 mt-2">Total paid</p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Receipt className="w-5 h-5 text-amber-600" />
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{stats.pendingSettlements || 0}</p>
                    <p className="text-sm text-gray-500 mt-1">Pending Settlements</p>
                    <button onClick={() => setCurrentTab('settlements')} className="text-xs text-amber-600 mt-2 hover:underline">View settlements →</button>
                  </div>
                </div>

                {/* Restaurant-wise Orders Breakdown */}
                {stats.ordersByRestaurant && stats.ordersByRestaurant.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Orders by Restaurant</h3>
                      <button
                        onClick={() => setCurrentTab('restaurants')}
                        className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                      >
                        View All →
                      </button>
                    </div>
                    <div className="space-y-3">
                      {stats.ordersByRestaurant.slice(0, 10).map((resto, index) => (
                        <div key={resto.restaurantId} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-sm font-bold text-orange-600">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{resto.restaurantName}</p>
                              <p className="text-sm text-gray-500">{resto.orderCount} orders</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">₹{resto.revenue.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">Revenue</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending Delivery Partner Applications */}
                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Pending Delivery Partners</h3>
                    <button
                      onClick={() => setCurrentTab('delivery_partners')}
                      className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                    >
                      View All →
                    </button>
                  </div>
                  {deliveryPartners.filter(dp => dp.verificationStatus === 'pending').length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No pending delivery partner applications</p>
                  ) : (
                    <div className="space-y-3">
                      {deliveryPartners.filter(dp => dp.verificationStatus === 'pending').slice(0, 5).map((partner) => (
                        <div 
                          key={partner.uid} 
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedPartner(partner);
                            setCurrentTab('delivery_partners');
                          }}
                        >
                          <div className="flex items-center gap-3">
                            {partner.profilePicture ? (
                              <img src={partner.profilePicture} alt={partner.name} className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                <User className="w-5 h-5 text-purple-600" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900">{partner.name}</p>
                              <p className="text-sm text-gray-500 capitalize">{partner.vehicleType} • {partner.phoneNumber}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">
                              {partner.createdAt?.toLocaleDateString?.() || 'N/A'}
                            </p>
                            <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full mt-1">
                              Pending
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent Pending Applications */}
                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Pending Applications</h3>
                    <button
                      onClick={() => setCurrentTab('applications')}
                      className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                    >
                      View All →
                    </button>
                  </div>
                  {applications.filter(a => a.status === 'pending').length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No pending applications</p>
                  ) : (
                    <div className="space-y-3">
                      {applications.filter(a => a.status === 'pending').slice(0, 5).map((app) => (
                        <div 
                          key={app.id} 
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedApplication(app);
                            setCurrentTab('applications');
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                              <Store className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{app.restaurantName}</p>
                              <p className="text-sm text-gray-500">{app.city}, {app.state}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">
                              {app.submittedAt.toLocaleDateString()}
                            </p>
                            <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full mt-1">
                              Pending
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent Orders */}
                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Orders</h3>
                    <button
                      onClick={() => setCurrentTab('orders')}
                      className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                    >
                      View All →
                    </button>
                  </div>
                  {orders.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No orders yet</p>
                  ) : (
                    <div className="space-y-3">
                      {orders.slice(0, 5).map((order) => (
                        <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                          <div>
                            <p className="font-medium text-gray-900">#{order.id.slice(-6).toUpperCase()}</p>
                            <p className="text-sm text-gray-500">{order.restaurantName}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">₹{order.totalAmount}</p>
                            <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                              order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                              order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {order.status.toUpperCase().replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Applications Tab */}
            {currentTab === 'applications' && (
              <motion.div
                key="applications"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                {/* Filters */}
                <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search applications..."
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex gap-2">
                      {(['all', 'pending', 'approved', 'rejected'] as const).map((filter) => (
                        <button
                          key={filter}
                          onClick={() => setAppFilter(filter)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors capitalize ${
                            appFilter === filter
                              ? 'bg-gray-900 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {filter}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Application Detail View */}
                {selectedApplication ? (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{selectedApplication.restaurantName}</h3>
                        <p className="text-gray-500">Application Details</p>
                      </div>
                      <button
                        onClick={() => setSelectedApplication(null)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <X className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>
                    
                    <div className="p-6 space-y-6">
                      {/* Status Banner */}
                      <div className={`p-4 rounded-xl ${
                        selectedApplication.status === 'pending' ? 'bg-yellow-50 border border-yellow-200' :
                        selectedApplication.status === 'approved' ? 'bg-green-50 border border-green-200' :
                        'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex items-center gap-3">
                          {selectedApplication.status === 'pending' && <Clock className="w-5 h-5 text-yellow-600" />}
                          {selectedApplication.status === 'approved' && <CheckCircle className="w-5 h-5 text-green-600" />}
                          {selectedApplication.status === 'rejected' && <XCircle className="w-5 h-5 text-red-600" />}
                          <span className={`font-semibold ${
                            selectedApplication.status === 'pending' ? 'text-yellow-800' :
                            selectedApplication.status === 'approved' ? 'text-green-800' :
                            'text-red-800'
                          }`}>
                            Status: {selectedApplication.status.toUpperCase()}
                          </span>
                        </div>
                        {selectedApplication.rejectionReason && (
                          <p className="mt-2 text-red-700">Reason: {selectedApplication.rejectionReason}</p>
                        )}
                      </div>

                      {/* Application Details Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900">Restaurant Information</h4>
                          <div className="space-y-3">
                            <div className="flex items-start gap-3">
                              <Store className="w-5 h-5 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-sm text-gray-500">Restaurant Name</p>
                                <p className="font-medium text-gray-900">{selectedApplication.restaurantName}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-sm text-gray-500">Owner Name</p>
                                <p className="font-medium text-gray-900">{selectedApplication.ownerName}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-sm text-gray-500">Email</p>
                                <p className="font-medium text-gray-900">{selectedApplication.email}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-sm text-gray-500">Phone</p>
                                <p className="font-medium text-gray-900">{selectedApplication.phoneNumber}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900">Location</h4>
                          <div className="space-y-3">
                            <div className="flex items-start gap-3">
                              <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-sm text-gray-500">Address</p>
                                <p className="font-medium text-gray-900">{selectedApplication.address}</p>
                                <p className="text-gray-600">{selectedApplication.city}, {selectedApplication.state} - {selectedApplication.pincode}</p>
                              </div>
                            </div>
                          </div>

                          <h4 className="font-semibold text-gray-900 mt-6">Cuisine Types</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedApplication.cuisine.map((c, i) => (
                              <span key={i} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                                {c}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Documents */}
                      <div className="pt-4 border-t border-gray-100">
                        <h4 className="font-semibold text-gray-900 mb-3">Documents</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="p-4 bg-gray-50 rounded-xl">
                            <p className="text-sm text-gray-500">FSSAI License</p>
                            <p className="font-medium text-gray-900">{selectedApplication.fssaiLicense}</p>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-xl">
                            <p className="text-sm text-gray-500">PAN Number</p>
                            <p className="font-medium text-gray-900">{selectedApplication.panNumber}</p>
                          </div>
                          {selectedApplication.gstNumber && (
                            <div className="p-4 bg-gray-50 rounded-xl">
                              <p className="text-sm text-gray-500">GST Number</p>
                              <p className="font-medium text-gray-900">{selectedApplication.gstNumber}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      <div className="pt-4 border-t border-gray-100">
                        <h4 className="font-semibold text-gray-900 mb-3">Description</h4>
                        <p className="text-gray-700">{selectedApplication.description}</p>
                      </div>

                      {/* Actions */}
                      {selectedApplication.status === 'pending' && (
                        <div className="pt-4 border-t border-gray-100 flex gap-4">
                          <button
                            onClick={() => handleApprove(selectedApplication)}
                            disabled={actionLoading === selectedApplication.id}
                            className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {actionLoading === selectedApplication.id ? (
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <>
                                <CheckCircle className="w-5 h-5" />
                                Approve Application
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => setShowRejectModal(true)}
                            disabled={actionLoading === selectedApplication.id}
                            className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <XCircle className="w-5 h-5" />
                            Reject Application
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Applications List */
                  <div className="space-y-4">
                    {filteredApplications.length === 0 ? (
                      <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">No applications found</p>
                      </div>
                    ) : (
                      filteredApplications.map((app) => (
                        <div 
                          key={app.id} 
                          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => setSelectedApplication(app)}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                                <Store className="w-7 h-7 text-orange-600" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 text-lg">{app.restaurantName}</h4>
                                <p className="text-gray-500">{app.ownerName} • {app.city}, {app.state}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  {app.cuisine.slice(0, 3).map((c, i) => (
                                    <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                      {c}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 sm:flex-col sm:items-end">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                app.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                app.status === 'approved' ? 'bg-green-100 text-green-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                              </span>
                              <p className="text-sm text-gray-400">
                                {app.submittedAt.toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Reject Modal */}
                <AnimatePresence>
                  {showRejectModal && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                      onClick={() => setShowRejectModal(false)}
                    >
                      <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="bg-white rounded-2xl shadow-xl w-full max-w-md"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-4">Reject Application</h3>
                          <p className="text-gray-600 mb-4">
                            Please provide a reason for rejecting this application. This will be sent to the applicant.
                          </p>
                          <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            rows={4}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            placeholder="Enter rejection reason..."
                          />
                          <div className="flex gap-3 mt-6">
                            <button
                              onClick={() => {
                                setShowRejectModal(false);
                                setRejectionReason('');
                              }}
                              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2.5 rounded-xl transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleReject}
                              disabled={actionLoading !== null}
                              className="flex-1 bg-red-500 hover:bg-red-600 text-white font-medium py-2.5 rounded-xl transition-colors disabled:opacity-50"
                            >
                              {actionLoading ? 'Rejecting...' : 'Reject'}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {/* Restaurants Tab */}
            {currentTab === 'restaurants' && (
              <motion.div
                key="restaurants"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                {/* Search */}
                <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search restaurants..."
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Restaurants Grid */}
                {filteredRestaurants.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                    <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No restaurants found</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredRestaurants.map((restaurant) => (
                      <div key={restaurant.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${restaurant.isActive ? 'bg-green-100' : 'bg-gray-100'}`}>
                                <Store className={`w-6 h-6 ${restaurant.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900">{restaurant.name}</h4>
                                <p className="text-sm text-gray-500">{restaurant.cuisine}</p>
                              </div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              restaurant.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {restaurant.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>

                          <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span>{restaurant.location.city}, {restaurant.location.state}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <span>{restaurant.phoneNumber}</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div className="text-sm">
                              <span className="text-gray-500">Orders: </span>
                              <span className="font-medium text-gray-900">{restaurant.totalOrders}</span>
                            </div>
                            <div className="text-sm">
                              <span className="text-gray-500">Revenue: </span>
                              <span className="font-medium text-gray-900">₹{restaurant.totalRevenue.toLocaleString()}</span>
                            </div>
                          </div>

                          {/* Commission & Linked Account */}
                          <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-3">
                            <div className="text-sm">
                              <span className="text-gray-500">Commission: </span>
                              <span className="font-semibold text-emerald-600">{restaurant.commissionPercentage ?? 10}%</span>
                            </div>
                            <div className="text-sm">
                              {restaurant.razorpayLinkedAccountId ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                                  <Link className="w-3 h-3" /> Linked
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 text-gray-500 rounded-full text-xs">
                                  Not linked
                                </span>
                              )}
                            </div>
                          </div>

                          <button
                            onClick={() => handleToggleRestaurantStatus(restaurant)}
                            disabled={actionLoading === restaurant.id}
                            className={`w-full mt-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              restaurant.isActive
                                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                : 'bg-green-100 text-green-600 hover:bg-green-200'
                            }`}
                          >
                            {actionLoading === restaurant.id ? 'Updating...' : restaurant.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Orders Tab */}
            {currentTab === 'orders' && (
              <motion.div
                key="orders"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                {orders.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                    <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No orders yet</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Order ID</th>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Restaurant</th>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Customer</th>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Amount</th>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {orders.map((order) => (
                            <tr key={order.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <span className="font-mono text-sm text-gray-900">#{order.id.slice(-6).toUpperCase()}</span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-gray-900">{order.restaurantName}</span>
                              </td>
                              <td className="px-6 py-4">
                                <div>
                                  <p className="text-gray-900">{order.customerName}</p>
                                  <p className="text-sm text-gray-500">{order.customerPhone}</p>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="font-medium text-gray-900">₹{order.totalAmount}</span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                  order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                  order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                  order.status === 'placed' ? 'bg-blue-100 text-blue-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {order.status.toUpperCase().replace('_', ' ')}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-gray-500">{order.orderedAt.toLocaleString()}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Delivery Partners Tab */}
            {currentTab === 'delivery_partners' && (
              <motion.div
                key="delivery_partners"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                {/* Filters */}
                <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search delivery partners..."
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex gap-2">
                      {(['all', 'pending', 'approved', 'rejected'] as const).map((filter) => (
                        <button
                          key={filter}
                          onClick={() => setDpFilter(filter)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors capitalize ${
                            dpFilter === filter
                              ? 'bg-gray-900 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {filter}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Selected Partner Detail View */}
                {selectedPartner ? (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {selectedPartner.profilePicture ? (
                          <img src={selectedPartner.profilePicture} alt={selectedPartner.name} className="w-16 h-16 rounded-full object-cover" />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{selectedPartner.name}</h3>
                          <p className="text-gray-500">{selectedPartner.phoneNumber}</p>
                        </div>
                      </div>
                      <button onClick={() => setSelectedPartner(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>

                    <div className="p-6 space-y-6">
                      {/* Status Banner */}
                      <div className={`p-4 rounded-xl ${
                        selectedPartner.verificationStatus === 'pending' ? 'bg-yellow-50 border border-yellow-200' :
                        selectedPartner.verificationStatus === 'approved' ? 'bg-green-50 border border-green-200' :
                        'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex items-center gap-3">
                          {selectedPartner.verificationStatus === 'pending' && <Clock className="w-5 h-5 text-yellow-600" />}
                          {selectedPartner.verificationStatus === 'approved' && <CheckCircle className="w-5 h-5 text-green-600" />}
                          {selectedPartner.verificationStatus === 'rejected' && <XCircle className="w-5 h-5 text-red-600" />}
                          <span className={`font-semibold ${
                            selectedPartner.verificationStatus === 'pending' ? 'text-yellow-800' :
                            selectedPartner.verificationStatus === 'approved' ? 'text-green-800' :
                            'text-red-800'
                          }`}>
                            Status: {selectedPartner.verificationStatus.toUpperCase()}
                          </span>
                        </div>
                        {selectedPartner.rejectionReason && (
                          <p className="mt-2 text-red-700">Reason: {selectedPartner.rejectionReason}</p>
                        )}
                      </div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900">Personal Information</h4>
                          <div className="space-y-3">
                            <div className="flex items-start gap-3">
                              <User className="w-5 h-5 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-sm text-gray-500">Full Name</p>
                                <p className="font-medium text-gray-900">{selectedPartner.name}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-sm text-gray-500">Phone</p>
                                <p className="font-medium text-gray-900">{selectedPartner.phoneNumber}</p>
                              </div>
                            </div>
                            {selectedPartner.email && (
                              <div className="flex items-start gap-3">
                                <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                                <div>
                                  <p className="text-sm text-gray-500">Email</p>
                                  <p className="font-medium text-gray-900">{selectedPartner.email}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900">Vehicle Information</h4>
                          <div className="space-y-3">
                            <div className="flex items-start gap-3">
                              <Bike className="w-5 h-5 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-sm text-gray-500">Vehicle Type</p>
                                <p className="font-medium text-gray-900 capitalize">{selectedPartner.vehicleType}</p>
                              </div>
                            </div>
                            {selectedPartner.vehicleNumber && (
                              <div className="flex items-start gap-3">
                                <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                                <div>
                                  <p className="text-sm text-gray-500">Vehicle Number</p>
                                  <p className="font-medium text-gray-900">{selectedPartner.vehicleNumber}</p>
                                </div>
                              </div>
                            )}
                          </div>

                          <h4 className="font-semibold text-gray-900 mt-4">Stats</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="p-3 bg-gray-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-gray-900">{selectedPartner.totalDeliveries || 0}</p>
                              <p className="text-xs text-gray-500">Deliveries</p>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-gray-900">₹{(selectedPartner.totalEarnings || 0).toLocaleString()}</p>
                              <p className="text-xs text-gray-500">Earnings</p>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-gray-900">⭐ {selectedPartner.rating?.toFixed(1) || '5.0'}</p>
                              <p className="text-xs text-gray-500">Rating</p>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-gray-900">{selectedPartner.isOnline ? '🟢' : '⚫'}</p>
                              <p className="text-xs text-gray-500">{selectedPartner.isOnline ? 'Online' : 'Offline'}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bank Details */}
                      <div className="pt-4 border-t border-gray-100">
                        <h4 className="font-semibold text-gray-900 mb-3">Bank Account (Payouts)</h4>
                        {selectedPartner.bankDetails ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-xs text-gray-500 mb-0.5">Account Holder</p>
                              <p className="font-medium text-gray-900 text-sm">{selectedPartner.bankDetails.accountHolderName}</p>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-xs text-gray-500 mb-0.5">Account Number</p>
                              <p className="font-medium text-gray-900 text-sm font-mono">{selectedPartner.bankDetails.accountNumber}</p>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-xs text-gray-500 mb-0.5">IFSC Code</p>
                              <p className="font-medium text-gray-900 text-sm font-mono">{selectedPartner.bankDetails.ifscCode}</p>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-xs text-gray-500 mb-0.5">Bank Name</p>
                              <p className="font-medium text-gray-900 text-sm">{selectedPartner.bankDetails.bankName}</p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-orange-500 italic">Bank details not provided</p>
                        )}
                      </div>

                      {/* Documents */}
                      <div className="pt-4 border-t border-gray-100">
                        <h4 className="font-semibold text-gray-900 mb-4">Photos & Documents</h4>

                        {/* Profile & Vehicle Photos */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div className="p-4 bg-gray-50 rounded-xl">
                            <p className="text-sm font-medium text-gray-500 mb-2">Profile Picture</p>
                            {selectedPartner.profilePicture ? (
                              <img
                                src={selectedPartner.profilePicture}
                                alt="Profile"
                                className="w-full h-48 rounded-xl object-cover cursor-pointer hover:opacity-90 transition border border-gray-200"
                                onClick={() => setLightboxImage(selectedPartner.profilePicture!)}
                              />
                            ) : (
                              <div className="w-full h-48 rounded-xl bg-gray-200 flex items-center justify-center">
                                <div className="text-center">
                                  <User className="w-10 h-10 text-gray-400 mx-auto mb-1" />
                                  <p className="text-xs text-gray-400">Not uploaded</p>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="p-4 bg-gray-50 rounded-xl">
                            <p className="text-sm font-medium text-gray-500 mb-2">Bike / Vehicle Photo</p>
                            {selectedPartner.vehicleImage ? (
                              <img
                                src={selectedPartner.vehicleImage}
                                alt="Vehicle"
                                className="w-full h-48 rounded-xl object-cover cursor-pointer hover:opacity-90 transition border border-gray-200"
                                onClick={() => setLightboxImage(selectedPartner.vehicleImage!)}
                              />
                            ) : (
                              <div className="w-full h-48 rounded-xl bg-gray-200 flex items-center justify-center">
                                <p className="text-xs text-gray-400">Not uploaded</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* ID Documents - Always show all 3, regardless of whether number is filled */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Aadhar Card */}
                          <div className="p-4 bg-gray-50 rounded-xl">
                            <p className="text-sm font-medium text-gray-500 mb-1">Aadhar Card</p>
                            {selectedPartner.aadharNumber ? (
                              <p className="font-semibold text-gray-900 mb-2 font-mono tracking-wide">{selectedPartner.aadharNumber}</p>
                            ) : (
                              <p className="text-xs text-orange-500 mb-2">Number not provided</p>
                            )}
                            {selectedPartner.aadharImage ? (
                              <img
                                src={selectedPartner.aadharImage}
                                alt="Aadhar Document"
                                className="w-full h-40 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition"
                                onClick={() => setLightboxImage(selectedPartner.aadharImage!)}
                              />
                            ) : (
                              <div className="w-full h-40 rounded-lg bg-gray-200 flex items-center justify-center">
                                <p className="text-xs text-gray-400">Image not uploaded</p>
                              </div>
                            )}
                          </div>

                          {/* PAN Card */}
                          <div className="p-4 bg-gray-50 rounded-xl">
                            <p className="text-sm font-medium text-gray-500 mb-1">PAN Card</p>
                            {selectedPartner.panNumber ? (
                              <p className="font-semibold text-gray-900 mb-2 font-mono tracking-wide">{selectedPartner.panNumber}</p>
                            ) : (
                              <p className="text-xs text-orange-500 mb-2">Number not provided</p>
                            )}
                            {selectedPartner.panImage ? (
                              <img
                                src={selectedPartner.panImage}
                                alt="PAN Document"
                                className="w-full h-40 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition"
                                onClick={() => setLightboxImage(selectedPartner.panImage!)}
                              />
                            ) : (
                              <div className="w-full h-40 rounded-lg bg-gray-200 flex items-center justify-center">
                                <p className="text-xs text-gray-400">Image not uploaded</p>
                              </div>
                            )}
                          </div>

                          {/* Driving License */}
                          <div className="p-4 bg-gray-50 rounded-xl">
                            <p className="text-sm font-medium text-gray-500 mb-1">Driving License</p>
                            {selectedPartner.drivingLicense ? (
                              <p className="font-semibold text-gray-900 mb-2 font-mono tracking-wide">{selectedPartner.drivingLicense}</p>
                            ) : (
                              <p className="text-xs text-orange-500 mb-2">Number not provided</p>
                            )}
                            {selectedPartner.drivingLicenseImage ? (
                              <img
                                src={selectedPartner.drivingLicenseImage}
                                alt="DL Document"
                                className="w-full h-40 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition"
                                onClick={() => setLightboxImage(selectedPartner.drivingLicenseImage!)}
                              />
                            ) : (
                              <div className="w-full h-40 rounded-lg bg-gray-200 flex items-center justify-center">
                                <p className="text-xs text-gray-400">Image not uploaded</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Registration info */}
                        {selectedPartner.createdAt && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100 flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-500" />
                            <span className="text-sm text-blue-700">
                              Registered: {(selectedPartner.createdAt as any)?.toDate?.()
                                ? (selectedPartner.createdAt as any).toDate().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : new Date(selectedPartner.createdAt as any).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Current Location */}
                      {selectedPartner.currentLocation && (
                        <div className="pt-4 border-t border-gray-100">
                          <h4 className="font-semibold text-gray-900 mb-2">Current Location</h4>
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <span>
                              {selectedPartner.currentLocation.latitude.toFixed(6)}, {selectedPartner.currentLocation.longitude.toFixed(6)}
                            </span>
                            <span className="text-xs text-gray-400">
                              (Last updated: {selectedPartner.currentLocation.lastUpdated?.toLocaleString?.() || 'N/A'})
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      {selectedPartner.verificationStatus === 'pending' && (
                        <div className="flex gap-3 pt-4 border-t border-gray-100">
                          <button
                            onClick={() => handleApprovePartner(selectedPartner)}
                            disabled={actionLoading === selectedPartner.uid}
                            className="flex-1 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <CheckCircle className="w-5 h-5" />
                            Approve Partner
                          </button>
                          <button
                            onClick={() => setShowDpRejectModal(true)}
                            disabled={actionLoading === selectedPartner.uid}
                            className="flex-1 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <XCircle className="w-5 h-5" />
                            Reject
                          </button>
                        </div>
                      )}

                      {/* Stop/Activate Account Button for approved partners */}
                      {selectedPartner.verificationStatus === 'approved' && (
                        <div className="flex gap-3 pt-4 border-t border-gray-100">
                          <button
                            onClick={() => handleTogglePartnerStatus(selectedPartner)}
                            disabled={actionLoading === selectedPartner.uid}
                            className={`flex-1 py-3 ${selectedPartner.isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2`}
                          >
                            {actionLoading === selectedPartner.uid ? (
                              'Processing...'
                            ) : selectedPartner.isActive ? (
                              <>
                                <Ban className="w-5 h-5" />
                                Stop Account
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-5 h-5" />
                                Activate Account
                              </>
                            )}
                          </button>
                        </div>
                      )}

                      {/* Account Status Indicator */}
                      {selectedPartner.verificationStatus === 'approved' && !selectedPartner.isActive && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700">
                          <Ban className="w-5 h-5" />
                          <span className="text-sm font-medium">This account is currently stopped/suspended</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Partners List */
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-4 border-b border-gray-100">
                      <h3 className="font-semibold text-gray-900">
                        Delivery Partners ({filteredDeliveryPartners.length})
                      </h3>
                    </div>
                    {filteredDeliveryPartners.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No delivery partners found</p>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {filteredDeliveryPartners.map((partner) => (
                          <div
                            key={partner.uid}
                            className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => setSelectedPartner(partner)}
                          >
                            <div className="flex items-center gap-4">
                              {partner.profilePicture ? (
                                <img src={partner.profilePicture} alt={partner.name} className="w-12 h-12 rounded-full object-cover" />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                                  <User className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-gray-900">{partner.name}</p>
                                <p className="text-sm text-gray-500">{partner.phoneNumber}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-gray-400 capitalize">{partner.vehicleType}</span>
                                  {partner.vehicleNumber && (
                                    <span className="text-xs text-gray-400">• {partner.vehicleNumber}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className="text-sm font-medium text-gray-900">{partner.totalDeliveries || 0} deliveries</p>
                                <p className="text-xs text-gray-500">⭐ {partner.rating?.toFixed(1) || '5.0'}</p>
                              </div>
                              {/* Account Status Badge */}
                              {partner.verificationStatus === 'approved' && !partner.isActive && (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                  Stopped
                                </span>
                              )}
                              <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                                partner.verificationStatus === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                partner.verificationStatus === 'approved' ? 'bg-green-100 text-green-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {partner.verificationStatus}
                              </span>
                              {partner.isOnline && (
                                <span className="w-3 h-3 bg-green-500 rounded-full" title="Online"></span>
                              )}
                              <Eye className="w-5 h-5 text-gray-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Reject Modal */}
                <AnimatePresence>
                  {showDpRejectModal && selectedPartner && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                      onClick={() => setShowDpRejectModal(false)}
                    >
                      <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="bg-white rounded-2xl p-6 max-w-md w-full"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Reject Application</h3>
                        <p className="text-gray-500 mb-4">
                          Please provide a reason for rejecting {selectedPartner.name}'s application.
                        </p>
                        <textarea
                          value={dpRejectionReason}
                          onChange={(e) => setDpRejectionReason(e.target.value)}
                          placeholder="Enter rejection reason..."
                          className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          rows={4}
                        />
                        <div className="flex gap-3 mt-4">
                          <button
                            onClick={() => setShowDpRejectModal(false)}
                            className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleRejectPartner}
                            disabled={actionLoading === selectedPartner.uid || !dpRejectionReason.trim()}
                            className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {/* Delivery Areas Tab */}
            {currentTab === 'delivery_areas' && (
              <motion.div
                key="delivery_areas"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                {/* Service Area Summary */}
                <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-6 text-white">
                  <h3 className="text-lg font-semibold mb-1">Service Areas</h3>
                  <p className="text-indigo-100 text-sm mb-4">Define the areas where your app operates. Only active areas will be shown to delivery partners and customers.</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white/15 backdrop-blur rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold">{deliveryAreas.length}</p>
                      <p className="text-xs text-indigo-100">Total Areas</p>
                    </div>
                    <div className="bg-white/15 backdrop-blur rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold">{deliveryAreas.filter(a => a.isActive).length}</p>
                      <p className="text-xs text-indigo-100">Active</p>
                    </div>
                    <div className="bg-white/15 backdrop-blur rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold">{deliveryAreas.filter(a => !a.isActive).length}</p>
                      <p className="text-xs text-indigo-100">Inactive</p>
                    </div>
                  </div>
                </div>

                {/* Add New Area */}
                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Add New Service Area</h3>
                  <p className="text-sm text-gray-500 mb-4">Define a new zone where your app will operate. Delivery partners will choose from these areas.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                    <input
                      type="text"
                      placeholder="Area name (e.g. Koregaon Park)"
                      value={newAreaName}
                      onChange={(e) => setNewAreaName(e.target.value)}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    />
                    <input
                      type="text"
                      placeholder="City (e.g. Pune)"
                      value={newAreaCity}
                      onChange={(e) => setNewAreaCity(e.target.value)}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Pincode (e.g. 411001)"
                      value={newAreaPincode}
                      onChange={(e) => setNewAreaPincode(e.target.value)}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={async () => {
                        if (!newAreaName.trim() || !newAreaCity.trim()) return;
                        setAreaActionLoading('creating');
                        try {
                          await createDeliveryArea({
                            name: newAreaName.trim(),
                            city: newAreaCity.trim(),
                            pincode: newAreaPincode.trim() || undefined,
                            isActive: true,
                          });
                          setNewAreaName('');
                          setNewAreaCity('');
                          setNewAreaPincode('');
                          const areas = await getAllDeliveryAreas();
                          setDeliveryAreas(areas);
                        } catch (err) {
                          console.error('Failed to create area:', err);
                        } finally {
                          setAreaActionLoading(null);
                        }
                      }}
                      disabled={!newAreaName.trim() || !newAreaCity.trim() || areaActionLoading === 'creating'}
                      className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 text-sm transition-colors"
                    >
                      {areaActionLoading === 'creating' ? 'Adding...' : 'Add Area'}
                    </button>
                    <p className="text-xs text-gray-400">New areas are active by default</p>
                  </div>
                </div>

                {/* Existing Areas */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">All Areas ({deliveryAreas.length})</h3>
                        <p className="text-sm text-gray-500">Toggle areas on/off to control where your app operates</p>
                      </div>
                    </div>
                  </div>
                  {deliveryAreas.length === 0 ? (
                    <div className="p-12 text-center">
                      <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No service areas created yet</p>
                      <p className="text-sm text-gray-400">Add areas above to define where your app operates</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {deliveryAreas.map((area) => {
                        const partnersInArea = deliveryPartners.filter(dp => dp.assignedArea === area.id);
                        const onlineInArea = partnersInArea.filter(dp => dp.isOnline);
                        return (
                          <div key={area.id} className={`p-4 flex items-center justify-between hover:bg-gray-50 transition-colors ${!area.isActive ? 'opacity-60' : ''}`}>
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${area.isActive ? 'bg-green-100' : 'bg-gray-100'}`}>
                                <MapPin className={`w-5 h-5 ${area.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-gray-900">{area.name}</p>
                                  {!area.isActive && (
                                    <span className="text-[10px] bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded font-medium uppercase">Paused</span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500">
                                  {area.city}{area.pincode ? ` • ${area.pincode}` : ''}
                                  <span className="mx-2">•</span>
                                  <span className="text-indigo-600">{partnersInArea.length} partners</span>
                                  <span className="mx-1">•</span>
                                  <span className="text-green-600">{onlineInArea.length} online</span>
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Toggle switch */}
                              <button
                                onClick={async () => {
                                  setAreaActionLoading(area.id);
                                  try {
                                    await updateDeliveryArea(area.id, { isActive: !area.isActive });
                                    const areas = await getAllDeliveryAreas();
                                    setDeliveryAreas(areas);
                                  } catch (err) { console.error(err); }
                                  finally { setAreaActionLoading(null); }
                                }}
                                disabled={areaActionLoading === area.id}
                                className={`relative w-11 h-6 rounded-full transition-colors ${
                                  area.isActive ? 'bg-green-500' : 'bg-gray-300'
                                } disabled:opacity-50`}
                              >
                                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                                  area.isActive ? 'translate-x-[22px]' : 'translate-x-0.5'
                                }`} />
                              </button>
                              <button
                                onClick={async () => {
                                  if (!confirm(`Delete "${area.name}"? Partners in this area will need to reselect.`)) return;
                                  setAreaActionLoading(area.id);
                                  try {
                                    await deleteDeliveryArea(area.id);
                                    const areas = await getAllDeliveryAreas();
                                    setDeliveryAreas(areas);
                                  } catch (err) { console.error(err); }
                                  finally { setAreaActionLoading(null); }
                                }}
                                disabled={areaActionLoading === area.id}
                                className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Info Card */}
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">How Service Areas Work</h4>
                  <ul className="text-sm text-blue-700 space-y-1.5">
                    <li>• <strong>Active areas</strong> are shown to delivery partners in the app for selection</li>
                    <li>• Delivery partners can only go online when they've selected an active area</li>
                    <li>• Orders are routed only to partners in the matching area</li>
                    <li>• <strong>Pausing an area</strong> hides it from new selections (existing partners stay assigned)</li>
                    <li>• <strong>Deleting an area</strong> removes it permanently — assigned partners will need to reselect</li>
                  </ul>
                </div>
              </motion.div>
            )}

            {/* Commission Tab */}
            {currentTab === 'commission' && (
              <motion.div
                key="commission"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                {/* Platform Commission Settings */}
                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-gray-600" />
                    Platform Commission Settings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Default Commission %</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={commissionSettings.defaultCommissionPercentage}
                        onChange={(e) => setCommissionSettings({ ...commissionSettings, defaultCommissionPercentage: Number(e.target.value) })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Min Commission %</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={commissionSettings.minCommissionPercentage}
                        onChange={(e) => setCommissionSettings({ ...commissionSettings, minCommissionPercentage: Number(e.target.value) })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Commission %</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={commissionSettings.maxCommissionPercentage}
                        onChange={(e) => setCommissionSettings({ ...commissionSettings, maxCommissionPercentage: Number(e.target.value) })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Partner Pay / Order (₹)</label>
                      <input
                        type="number"
                        min={0}
                        value={commissionSettings.deliveryPartnerPayPerOrder}
                        onChange={(e) => setCommissionSettings({ ...commissionSettings, deliveryPartnerPayPerOrder: Number(e.target.value) })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Partner Pay / km (₹)</label>
                      <input
                        type="number"
                        min={0}
                        value={commissionSettings.deliveryPartnerPayPerKm}
                        onChange={(e) => setCommissionSettings({ ...commissionSettings, deliveryPartnerPayPerKm: Number(e.target.value) })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={async () => {
                          setCommissionSaving(true);
                          try {
                            await updatePlatformCommissionSettings(commissionSettings);
                            alert('Commission settings saved!');
                          } catch (err: any) {
                            alert('Error: ' + err.message);
                          } finally {
                            setCommissionSaving(false);
                          }
                        }}
                        disabled={commissionSaving}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {commissionSaving ? 'Saving...' : 'Save Settings'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Restaurant Commission Table — ranked by commission (highest first = top listed) */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-6 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      Restaurant Commission Ranking
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Restaurants with higher commission are listed higher in the customer app. Edit commission to change ranking.
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 text-left text-sm font-medium text-gray-500">
                          <th className="px-6 py-4">#</th>
                          <th className="px-6 py-4">Restaurant</th>
                          <th className="px-6 py-4">City</th>
                          <th className="px-6 py-4">Orders</th>
                          <th className="px-6 py-4">Revenue</th>
                          <th className="px-6 py-4">
                            <span className="flex items-center gap-1">Commission % <ArrowUpDown className="w-3 h-3" /></span>
                          </th>
                          <th className="px-6 py-4">Razorpay Account</th>
                          <th className="px-6 py-4">Status</th>
                          <th className="px-6 py-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {[...restaurants]
                          .sort((a, b) => (b.commissionPercentage || 0) - (a.commissionPercentage || 0))
                          .map((r, idx) => (
                            <tr key={r.id || r.uid} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                  idx < 3 ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {idx + 1}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div>
                                  <p className="font-medium text-gray-900">{r.name}</p>
                                  <p className="text-xs text-gray-400">{r.email}</p>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600">{r.location?.city || '—'}</td>
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">{r.totalOrders}</td>
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">₹{(r.totalRevenue || 0).toLocaleString()}</td>
                              <td className="px-6 py-4">
                                {editingCommission?.id === (r.id || r.uid) ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      min={commissionSettings.minCommissionPercentage}
                                      max={commissionSettings.maxCommissionPercentage}
                                      value={editingCommission.value}
                                      onChange={(e) => setEditingCommission({ ...editingCommission, value: Number(e.target.value) })}
                                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-400"
                                    />
                                    <button
                                      onClick={async () => {
                                        setCommissionSaving(true);
                                        try {
                                          await updateRestaurantCommission(r.id || r.uid, editingCommission.value);
                                          setEditingCommission(null);
                                          await loadData();
                                        } catch (err: any) {
                                          alert('Error: ' + err.message);
                                        } finally {
                                          setCommissionSaving(false);
                                        }
                                      }}
                                      disabled={commissionSaving}
                                      className="text-green-600 hover:text-green-700"
                                    >
                                      <CheckCircle className="w-5 h-5" />
                                    </button>
                                    <button
                                      onClick={() => setEditingCommission(null)}
                                      className="text-gray-400 hover:text-gray-500"
                                    >
                                      <XCircle className="w-5 h-5" />
                                    </button>
                                  </div>
                                ) : (
                                  <span
                                    onClick={() => setEditingCommission({ id: r.id || r.uid, value: r.commissionPercentage ?? 10 })}
                                    className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-semibold cursor-pointer hover:bg-emerald-100 transition-colors"
                                  >
                                    {r.commissionPercentage ?? 10}%
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                {r.razorpayLinkedAccountId ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                                    <Link className="w-3 h-3" />
                                    {r.razorpayAccountStatus || 'created'}
                                  </span>
                                ) : (
                                  <button
                                    onClick={async () => {
                                      if (!confirm(`Create Razorpay linked account for ${r.name}?`)) return;
                                      setLinkingAccount(r.id || r.uid);
                                      try {
                                        await createLinkedAccount(r.id || r.uid, 'restaurant', {
                                          email: r.email || `${(r.id || r.uid)}@lomra.in`,
                                          phone: r.phoneNumber || '9999999999',
                                          legalBusinessName: r.name || 'Restaurant',
                                          contactName: r.bankDetails?.accountHolderName || r.name,
                                          bankAccount: r.bankDetails ? {
                                            ifscCode: r.bankDetails.ifscCode,
                                            beneficiaryName: r.bankDetails.accountHolderName,
                                            accountNumber: r.bankDetails.accountNumber,
                                            accountType: 'current',
                                          } : undefined,
                                        });
                                        await loadData();
                                        alert('Linked account created!');
                                      } catch (err: any) {
                                        const msg = err.message || '';
                                        if (msg.includes('Route') && msg.includes('not enabled')) {
                                          alert('Razorpay Route is not enabled on your merchant account.\n\nPlease enable it at: Razorpay Dashboard → Account & Settings → Product Configuration → Route\n\nOnce enabled, you can create linked accounts for restaurants.');
                                        } else {
                                          alert('Error creating linked account: ' + msg);
                                        }
                                      } finally {
                                        setLinkingAccount(null);
                                      }
                                    }}
                                    disabled={linkingAccount === (r.id || r.uid)}
                                    className="text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded-full hover:bg-blue-100 transition-colors disabled:opacity-50"
                                  >
                                    {linkingAccount === (r.id || r.uid) ? 'Creating...' : '+ Create Account'}
                                  </button>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  r.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {r.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <button
                                  onClick={() => setEditingCommission({ id: r.id || r.uid, value: r.commissionPercentage ?? 10 })}
                                  className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                                >
                                  Edit
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {restaurants.length === 0 && (
                    <div className="p-8 text-center text-gray-400">
                      <Store className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      No restaurants found
                    </div>
                  )}
                </div>

                {/* How Commission Ranking Works */}
                <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
                  <h4 className="font-semibold text-blue-900 mb-2">How Commission Ranking Works</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Restaurants with <strong>higher commission %</strong> are ranked higher in the customer app</li>
                    <li>• Higher-ranked restaurants appear first and get "Recommended" badges</li>
                    <li>• Commission is tracked in settlements — manual payouts until Razorpay Route is enabled</li>
                    <li>• Default commission for new restaurants: <strong>{commissionSettings.defaultCommissionPercentage}%</strong></li>
                  </ul>
                </div>
                <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
                  <h4 className="font-semibold text-amber-900 mb-2">⚠️ Razorpay Route Status</h4>
                  <p className="text-sm text-amber-800">
                    Razorpay Route (auto payment splitting) must be enabled on your merchant account. 
                    Go to <strong>Razorpay Dashboard → Account &amp; Settings → Product Configuration → Route</strong> to request access. 
                    Until enabled, commission is tracked here and settlements can be generated, but payment splitting won't happen automatically.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Settlements Tab */}
            {currentTab === 'settlements' && (
              <motion.div
                key="settlements"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                {/* Generate Settlement */}
                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-gray-600" />
                    Generate Settlement / Invoice
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant</label>
                      <select
                        value={settlementRestaurant}
                        onChange={(e) => setSettlementRestaurant(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400"
                      >
                        <option value="">Select Restaurant</option>
                        {restaurants.map((r) => (
                          <option key={r.id || r.uid} value={r.id || r.uid}>{r.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Period Start</label>
                      <input
                        type="date"
                        value={settlementPeriodStart}
                        onChange={(e) => setSettlementPeriodStart(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Period End</label>
                      <input
                        type="date"
                        value={settlementPeriodEnd}
                        onChange={(e) => setSettlementPeriodEnd(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={async () => {
                          if (!settlementRestaurant || !settlementPeriodStart || !settlementPeriodEnd) {
                            alert('Please fill all fields');
                            return;
                          }
                          setSettlementGenerating(true);
                          try {
                            await generateSettlement(
                              settlementRestaurant,
                              new Date(settlementPeriodStart).toISOString(),
                              new Date(settlementPeriodEnd).toISOString()
                            );
                            await loadData();
                            setSettlementRestaurant('');
                            setSettlementPeriodStart('');
                            setSettlementPeriodEnd('');
                            alert('Settlement generated!');
                          } catch (err: any) {
                            alert('Error: ' + err.message);
                          } finally {
                            setSettlementGenerating(false);
                          }
                        }}
                        disabled={settlementGenerating}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {settlementGenerating ? 'Generating...' : 'Generate'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Settlements List */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-6 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900">Settlement History</h3>
                  </div>

                  {settlements.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                      <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      No settlements generated yet
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 text-left text-sm font-medium text-gray-500">
                            <th className="px-6 py-4">Restaurant</th>
                            <th className="px-6 py-4">Period</th>
                            <th className="px-6 py-4">Orders</th>
                            <th className="px-6 py-4">Order Value</th>
                            <th className="px-6 py-4">Commission %</th>
                            <th className="px-6 py-4">Platform Earned</th>
                            <th className="px-6 py-4">Restaurant Payout</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {settlements.map((s) => (
                            <tr key={s.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <p className="font-medium text-gray-900">{s.restaurantName}</p>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600">{s.period}</td>
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">{s.totalOrders}</td>
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">₹{(s.totalOrderValue || 0).toLocaleString()}</td>
                              <td className="px-6 py-4">
                                <span className="text-sm font-semibold text-emerald-600">{s.commissionPercentage}%</span>
                              </td>
                              <td className="px-6 py-4 text-sm font-semibold text-emerald-600">₹{(s.platformCommission || 0).toLocaleString()}</td>
                              <td className="px-6 py-4 text-sm font-medium text-blue-600">₹{(s.restaurantPayout || 0).toLocaleString()}</td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  s.status === 'settled' ? 'bg-green-50 text-green-700' :
                                  s.status === 'processing' ? 'bg-blue-50 text-blue-700' :
                                  s.status === 'failed' ? 'bg-red-50 text-red-700' :
                                  'bg-yellow-50 text-yellow-700'
                                }`}>
                                  {s.status}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                {s.status === 'pending' && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={async () => {
                                        try {
                                          await updateSettlementStatus(s.id, 'settled');
                                          await loadData();
                                        } catch (err: any) {
                                          alert('Error: ' + err.message);
                                        }
                                      }}
                                      className="text-xs bg-green-50 text-green-600 px-3 py-1 rounded-full hover:bg-green-100"
                                    >
                                      Mark Settled
                                    </button>
                                  </div>
                                )}
                                {s.status === 'settled' && (
                                  <span className="text-xs text-gray-400">
                                    {s.settledAt ? new Date(s.settledAt).toLocaleDateString() : '—'}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Settings Tab */}
            {currentTab === 'settings' && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Information</h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Admin UID</p>
                      <p className="font-mono text-gray-900">{adminUid}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Role</p>
                      <p className="font-medium text-gray-900">Administrator</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Settings</h3>
                  <div className="space-y-6">
                    {/* Max Order Limit */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-900">Maximum Orders per Delivery Partner</p>
                          <p className="text-sm text-gray-500">Set how many orders a delivery partner can accept simultaneously</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-3">
                        <input
                          type="number"
                          min={1}
                          max={20}
                          value={deliverySettings.maxOrderLimit}
                          onChange={(e) => setDeliverySettings(prev => ({
                            ...prev,
                            maxOrderLimit: Math.max(1, Math.min(20, parseInt(e.target.value) || 1))
                          }))}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-center text-lg font-semibold"
                        />
                        <span className="text-sm text-gray-500">orders at a time</span>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end">
                      <button
                        onClick={async () => {
                          setSettingsLoading(true);
                          try {
                            await updateDeliverySettings(deliverySettings);
                            setSettingsSaved(true);
                            setTimeout(() => setSettingsSaved(false), 3000);
                          } catch (err) {
                            console.error('Failed to save settings:', err);
                          } finally {
                            setSettingsLoading(false);
                          }
                        }}
                        disabled={settingsLoading}
                        className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 text-sm"
                      >
                        {settingsLoading ? 'Saving...' : settingsSaved ? '✓ Saved' : 'Save Settings'}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Notifications Tab */}
            {currentTab === 'notifications' && (
              <motion.div
                key="notifications"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-6 max-w-3xl"
              >
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                  {/* Header */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Bell className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Send Push Notification</h3>
                        <p className="text-sm text-gray-500">Broadcast to selected apps and users</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-8">
                    {/* ── Step 1: Target Apps ── */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                        Step 1 · Select Target Apps
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {([
                          { id: 'delivery' as AppType,   label: 'Delivery App',    icon: Truck,  sel: 'border-blue-500 bg-blue-50',   unsel: 'border-gray-200', iBg: 'bg-blue-100',   iText: 'text-blue-600',   tText: 'text-blue-700',   chk: 'text-blue-500' },
                          { id: 'restaurant' as AppType, label: 'Restaurant App',  icon: Store,  sel: 'border-orange-500 bg-orange-50', unsel: 'border-gray-200', iBg: 'bg-orange-100', iText: 'text-orange-600', tText: 'text-orange-700', chk: 'text-orange-500' },
                          { id: 'customer' as AppType,   label: 'Customer App',    icon: Users,  sel: 'border-green-500 bg-green-50',  unsel: 'border-gray-200', iBg: 'bg-green-100',  iText: 'text-green-600',  tText: 'text-green-700',  chk: 'text-green-500' },
                        ]).map(({ id, label, icon: Icon, sel, unsel, iBg, iText, tText, chk }) => {
                          const selected = notifApps.includes(id);
                          return (
                            <button
                              key={id}
                              onClick={() => {
                                setNotifApps(prev => selected ? prev.filter(a => a !== id) : [...prev, id]);
                                setNotifSelectedUsers([]);
                                setNotifResult(null);
                              }}
                              className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${selected ? sel : unsel + ' hover:border-gray-300'}`}
                            >
                              <div className={`p-2 rounded-lg ${selected ? iBg : 'bg-gray-100'}`}>
                                <Icon className={`w-5 h-5 ${selected ? iText : 'text-gray-500'}`} />
                              </div>
                              <span className={`font-medium text-sm ${selected ? tText : 'text-gray-700'}`}>{label}</span>
                              {selected && <CheckCircle className={`w-4 h-4 ${chk} ml-auto flex-shrink-0`} />}
                            </button>
                          );
                        })}
                      </div>
                      {notifApps.length === 0 && (
                        <p className="text-xs text-amber-600 mt-2">Select at least one app to continue.</p>
                      )}
                    </div>

                    {/* ── Step 2: Recipients ── */}
                    {notifApps.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                          Step 2 · Select Recipients
                        </p>
                        <div className="flex gap-3 mb-4">
                          {(['all', 'specific'] as const).map((mode) => (
                            <button
                              key={mode}
                              onClick={() => { setNotifUserMode(mode); setNotifSelectedUsers([]); }}
                              className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                                notifUserMode === mode
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              {mode === 'all' ? '🌐 All Users' : '🎯 Specific Users'}
                            </button>
                          ))}
                        </div>

                        {notifUserMode === 'specific' && (
                          <div className="border border-gray-200 rounded-xl overflow-hidden">
                            {/* Search bar */}
                            <div className="p-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                              <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <input
                                value={notifUserSearch}
                                onChange={e => setNotifUserSearch(e.target.value)}
                                placeholder="Search by name, email or phone…"
                                className="flex-1 bg-transparent text-sm outline-none"
                              />
                              {notifUsers.length > 0 && (
                                <button
                                  onClick={() => {
                                    const visible = notifUsers.filter(u =>
                                      !notifUserSearch ||
                                      u.name.toLowerCase().includes(notifUserSearch.toLowerCase()) ||
                                      u.email?.toLowerCase().includes(notifUserSearch.toLowerCase()) ||
                                      u.phone?.includes(notifUserSearch)
                                    );
                                    const allSelected = visible.every(u => notifSelectedUsers.includes(u.uid));
                                    setNotifSelectedUsers(prev =>
                                      allSelected
                                        ? prev.filter(id => !visible.find(u => u.uid === id))
                                        : [...new Set([...prev, ...visible.map(u => u.uid)])]
                                    );
                                  }}
                                  className="text-xs text-purple-600 font-medium hover:underline flex-shrink-0"
                                >
                                  Select All Visible
                                </button>
                              )}
                            </div>

                            {/* User list */}
                            <div className="max-h-72 overflow-y-auto">
                              {notifUsersLoading ? (
                                <div className="flex items-center justify-center py-8">
                                  <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mr-2" />
                                  <span className="text-sm text-gray-500">Loading users…</span>
                                </div>
                              ) : notifUsers.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-8">No users found for selected apps.</p>
                              ) : (
                                notifUsers
                                  .filter(u =>
                                    !notifUserSearch ||
                                    u.name.toLowerCase().includes(notifUserSearch.toLowerCase()) ||
                                    u.email?.toLowerCase().includes(notifUserSearch.toLowerCase()) ||
                                    u.phone?.includes(notifUserSearch)
                                  )
                                  .map(user => {
                                    const checked = notifSelectedUsers.includes(user.uid);
                                    const appLabel = { delivery: 'Delivery', restaurant: 'Restro', customer: 'Customer' }[user.app];
                                    const appColor = { delivery: 'bg-blue-100 text-blue-700', restaurant: 'bg-orange-100 text-orange-700', customer: 'bg-green-100 text-green-700' }[user.app];
                                    return (
                                      <label
                                        key={user.uid + user.app}
                                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-gray-50 hover:bg-gray-50 transition-colors ${checked ? 'bg-purple-50' : ''}`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() =>
                                            setNotifSelectedUsers(prev =>
                                              checked ? prev.filter(id => id !== user.uid) : [...prev, user.uid]
                                            )
                                          }
                                          className="w-4 h-4 accent-purple-600"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 truncate">{user.name || 'Unnamed'}</p>
                                          <p className="text-xs text-gray-400 truncate">{user.email || user.phone || user.uid}</p>
                                        </div>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${appColor}`}>{appLabel}</span>
                                      </label>
                                    );
                                  })
                              )}
                            </div>

                            {notifSelectedUsers.length > 0 && (
                              <div className="px-4 py-2 bg-purple-50 border-t border-purple-100 text-xs text-purple-700 font-medium">
                                {notifSelectedUsers.length} user{notifSelectedUsers.length !== 1 ? 's' : ''} selected
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Step 3: Notification Form ── */}
                    {notifApps.length > 0 && (notifUserMode === 'all' || notifSelectedUsers.length > 0) && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                          Step 3 · Compose Notification
                        </p>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
                            <input
                              value={notifTitle}
                              onChange={e => setNotifTitle(e.target.value)}
                              placeholder="e.g. Special Offer Just For You!"
                              maxLength={100}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Message <span className="text-red-500">*</span></label>
                            <textarea
                              value={notifBody}
                              onChange={e => setNotifBody(e.target.value)}
                              placeholder="Write your notification message here…"
                              maxLength={300}
                              rows={3}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                            />
                            <p className="text-xs text-gray-400 mt-1 text-right">{notifBody.length}/300</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Image URL <span className="text-gray-400 font-normal">(optional)</span></label>
                            <input
                              value={notifImageUrl}
                              onChange={e => setNotifImageUrl(e.target.value)}
                              placeholder="https://…"
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Send Button + Result ── */}
                    {notifApps.length > 0 && (notifUserMode === 'all' || notifSelectedUsers.length > 0) && (
                      <div className="pt-2 border-t border-gray-100">
                        {notifResult && (
                          <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
                            notifResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                          }`}>
                            {notifResult.success ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <XCircle className="w-4 h-4 flex-shrink-0" />}
                            {notifResult.message}
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <div className="text-sm text-gray-500">
                            Sending to: <span className="font-medium text-gray-800">
                              {notifUserMode === 'all' ? `All users in ${notifApps.join(', ')} app${notifApps.length > 1 ? 's' : ''}` : `${notifSelectedUsers.length} selected user${notifSelectedUsers.length !== 1 ? 's' : ''}`}
                            </span>
                          </div>
                          <button
                            disabled={notifSending || !notifTitle.trim() || !notifBody.trim()}
                            onClick={async () => {
                              if (!notifTitle.trim() || !notifBody.trim()) return;
                              setNotifSending(true);
                              setNotifResult(null);
                              try {
                                const payload: SendNotificationPayload = {
                                  apps: notifApps,
                                  userIds: notifUserMode === 'all' ? 'all' : notifSelectedUsers,
                                  title: notifTitle.trim(),
                                  body: notifBody.trim(),
                                  ...(notifImageUrl.trim() ? { imageUrl: notifImageUrl.trim() } : {}),
                                };
                                const result = await sendNotification(payload);
                                const failInfo = result.failures?.length
                                  ? `\nErrors: ${result.failures.map((f: any) => f.error).join(', ')}`
                                  : '';
                                setNotifResult({ success: result.sent > 0, message: result.message + failInfo });
                              } catch (err: any) {
                                setNotifResult({ success: false, message: err.message || 'Failed to send notification' });
                              } finally {
                                setNotifSending(false);
                              }
                            }}
                            className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                          >
                            {notifSending ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Sending…
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4" />
                                Send Notification
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Image Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 rounded-full transition"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={lightboxImage}
            alt="Document preview"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <a
            href={lightboxImage}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-6 right-6 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg transition"
            onClick={(e) => e.stopPropagation()}
          >
            Open Original ↗
          </a>
        </div>
      )}
    </div>
  );
}
